import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Download, Printer } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  DEALERSHIP_NAME,
  type StockOrderReport,
  type StockOrderReportLine,
  type StockOrderReportPart,
  type StockOrderSideFilter,
} from "../../../../shared/contracts.js";
import { request } from "../../../api/http.js";
import { AsyncPanel } from "../../../ui/AsyncPanel.js";
import { Button } from "../../../ui/Button.js";
import { TextField } from "../../../ui/FormField.js";
import { etaLabel } from "../parts-actions/VerifyArrivalDialog.js";
import { formatCount, roLabel, technicianLabel, vinEndingLabel } from "../presentation.js";
import { caseWorkspacePath, formatReceiptInstant } from "../receipt/OrderReceiptPage.js";
import { wholesaleOrderPath } from "../../wholesale/presentation.js";
import { SidedPageShell } from "../../wholesale/SidedPageShell.js";
import {
  csvFileName,
  downloadTextFile,
  earliestStartFor,
  formatRangeLabel,
  latestEndFor,
  lineStatusLabel,
  parseReportUrlState,
  RANGE_PRESETS,
  RANGE_PRESET_LABELS,
  reportApiPath,
  resolveRange,
  serializeReportUrlState,
  SIDE_FILTER_LABELS,
  SIDE_FILTERS,
  sideLabel,
  stockOrderCsv,
  todayAtDealership,
  vehicleLabel,
  type RangePreset,
  type ReportUrlState,
  type ResolvedRange,
} from "./stock-order-report.js";

/**
 * `/parts/reports/stock-orders` and `/wholesale/reports/stock-orders` —
 * the stock order report (owner request 2026-09-17): every part line
 * ordered in a range of days, for the parts manager of the day to place
 * and reconcile the dealership's stock order.
 *
 * The page sits in the shell of the side it was opened from (the address
 * says which — `SidedPageShell`), beside that side's order form. A range bar
 * offers Today · This week · This month · Custom (two date inputs) and,
 * since the wholesale side (wholesale spec §8), a **Side** filter — Both ·
 * Service · Wholesale; the chosen range and side are the address
 * (`?range=…&from=…&to=…&side=…`), so a bookmark or a printed page's URL
 * reopens the same report, and the presets resolve to explicit dates in
 * the dealership's zone before the request goes out. The Lines table gains
 * a Side column, and a wholesale line shows its WS order (linked into the
 * wholesale workspace), the customer, its reference and R&R number in
 * place of the vehicle and the people.
 * Below it: a summary strip of the server's totals, the "By part" table
 * (one row per part number — the stock order itself, with the quantities of
 * cancelled lines in their own column and never in the ordered one) and the
 * "Lines" table (every line with its order, linked into the Parts
 * workspace). Everything shown is what `GET /api/reports/stock-orders`
 * answered; the page derives nothing but the words.
 *
 * *Print* is the browser's own dialog over `report.css`'s print half —
 * the range bar and buttons disappear, the header names the dealership,
 * the report and the range, and the browser repeats each table's headings
 * on every sheet. *Download CSV* writes the lines table from the loaded report as
 * `stock-orders-<from>-<to>.csv`. The page is Parts' own — the shell sends
 * any other role home — and performs no mutation.
 */

/** Under its own root: no Parts action invalidates it, the range and side in the key do. */
export const stockOrderReportQueryKey = (range: ResolvedRange, side: StockOrderSideFilter = "both") =>
  ["reports", "stock-orders", range.from, range.to, side] as const;

export function fetchStockOrderReport(range: ResolvedRange, side: StockOrderSideFilter = "both"): Promise<StockOrderReport> {
  return request<StockOrderReport>(reportApiPath(range, side));
}

export function useStockOrderReport(range: ResolvedRange, side: StockOrderSideFilter = "both"): UseQueryResult<StockOrderReport> {
  return useQuery({
    queryKey: stockOrderReportQueryKey(range, side),
    queryFn: () => fetchStockOrderReport(range, side),
  });
}

export function StockOrderReportPage() {
  return (
    <SidedPageShell
      activeDestination="stock_order_report"
      className="report-shell"
      chromeClassName="screen-only"
      mainClassName="report-page"
    >
      <ReportContent />
    </SidedPageShell>
  );
}

/**
 * The range from the URL, the request it means, and the report.
 *
 * "Today" is read once per mount from the browser's clock in the
 * dealership's zone; a page left open over midnight keeps the day it was
 * opened on until it is reloaded, which is what a printed report wants.
 */
function ReportContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseReportUrlState(searchParams), [searchParams]);
  const [today] = useState(() => todayAtDealership());
  const range = useMemo(() => resolveRange(state, today), [state, today]);
  const query = useStockOrderReport(range, state.side);
  const report = query.data;
  const titleId = useId();

  const writeState = useCallback(
    (next: ReportUrlState, options: { replace?: boolean } = {}) => {
      setSearchParams(serializeReportUrlState(next), { replace: options.replace ?? false });
    },
    [setSearchParams],
  );

  const choosePreset = (preset: RangePreset) => {
    if (preset === state.range) return;
    // Custom starts from the dates that were on screen, so nothing jumps; the side stays.
    writeState(
      preset === "custom"
        ? { range: "custom", from: range.from, to: range.to, side: state.side }
        : { range: preset, from: null, to: null, side: state.side },
    );
  };

  const chooseSide = (side: StockOrderSideFilter) => {
    if (side === state.side) return;
    writeState({ ...state, side }, { replace: true });
  };

  // The address always spells the range out, so a bare `/parts/reports/stock-orders`
  // becomes `?range=day` and a malformed custom range its repaired form.
  useEffect(() => {
    if (serializeReportUrlState(state).toString() !== searchParams.toString()) {
      writeState(state, { replace: true });
    }
  }, [state, searchParams, writeState]);

  const setDate = (field: "from" | "to", value: string) => {
    // A date input reports "" while a date is half-typed; the address only ever holds whole dates.
    if (value === "") return;
    // Moving one end past the other, or further from it than the longest report, drags the other
    // end along rather than asking for a range the server refuses (the pickers say so too, below).
    const from =
      field === "from" ? value : value < range.from ? value : value > latestEndFor(range.from) ? earliestStartFor(value) : range.from;
    const to =
      field === "to" ? value : value > range.to ? value : value < earliestStartFor(range.to) ? latestEndFor(value) : range.to;
    writeState({ range: "custom", from, to, side: state.side }, { replace: true });
  };

  // The print dialog's default file name is the document title.
  useEffect(() => {
    const previous = document.title;
    document.title = `Stock order report ${range.from}${range.from === range.to ? "" : ` to ${range.to}`}`;
    return () => {
      document.title = previous;
    };
  }, [range]);

  const empty = report !== undefined && report.lines.length === 0;
  // The sheet's "Printed" instant is when the rows on it were fetched: it moves with every refetch.
  const printedAt = useMemo(() => new Date().toISOString(), [report]);

  return (
    <div className="report" aria-labelledby={titleId}>
      <header className="report__header">
        <div className="report__heading">
          <p className="report__kicker">
            <span className="report__dealership">{DEALERSHIP_NAME}</span>
            <span className="report__kicker-sep" aria-hidden="true">
              ·
            </span>
            Parts
          </p>
          <h1 id={titleId} className="report__title">
            Stock order report
          </h1>
          <p className="report__lead">
            Every {state.side === "both" ? "" : `${SIDE_FILTER_LABELS[state.side].toLowerCase()} `}part line ordered{" "}
            <strong className="report__range-label">{formatRangeLabel(range)}</strong>, by the day it was
            added to its order. Cancelled lines stay listed and flagged.
          </p>
        </div>
        <p className="report__printed print-only">
          Printed <time dateTime={printedAt}>{formatReceiptInstant(printedAt)}</time>
        </p>
      </header>

      <div className="report__bar screen-only">
        <div className="report__range" role="group" aria-label="Range">
          <div className="report__presets">
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="report__preset"
                aria-pressed={state.range === preset}
                onClick={() => choosePreset(preset)}
              >
                {RANGE_PRESET_LABELS[preset]}
              </button>
            ))}
          </div>
          {state.range === "custom" ? (
            <div className="report__dates">
              <TextField
                id="report-from"
                label="From"
                type="date"
                value={range.from}
                min={earliestStartFor(range.to)}
                max={range.to}
                onChange={(event) => setDate("from", event.target.value)}
                fieldClassName="report__date"
              />
              <TextField
                id="report-to"
                label="To"
                type="date"
                value={range.to}
                min={range.from}
                max={latestEndFor(range.from)}
                onChange={(event) => setDate("to", event.target.value)}
                fieldClassName="report__date"
              />
            </div>
          ) : null}
        </div>
        <div className="report__side" role="group" aria-label="Side">
          <div className="report__presets">
            {SIDE_FILTERS.map((side) => (
              <button
                key={side}
                type="button"
                className="report__preset"
                aria-pressed={state.side === side}
                onClick={() => chooseSide(side)}
              >
                {SIDE_FILTER_LABELS[side]}
              </button>
            ))}
          </div>
        </div>
        <div className="report__actions">
          <Button
            variant="secondary"
            icon={<Download />}
            disabled={!report || empty}
            onClick={() => {
              if (!report) return;
              downloadTextFile(csvFileName(range), stockOrderCsv(report), "text/csv;charset=utf-8");
            }}
          >
            Download CSV
          </Button>
          <Button variant="primary" icon={<Printer />} disabled={!report} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>

      <AsyncPanel
        isLoading={query.isPending}
        error={query.isError && !report ? query.error : undefined}
        refreshError={query.isError && report ? query.error : undefined}
        refreshTitle="Couldn't refresh this report."
        onRetry={() => {
          void query.refetch();
        }}
        loadingLabel="Loading report…"
        isEmpty={empty}
        emptyTitle="No parts were ordered in this range."
        emptyDescription="Lines appear here on the day they are added to an order. Try another range."
      >
        {report ? <ReportBody report={report} /> : null}
      </AsyncPanel>
    </div>
  );
}

function ReportBody({ report }: { report: StockOrderReport }) {
  const summaryId = useId();
  return (
    <>
      <section className="report__summary" aria-labelledby={summaryId}>
        <h2 id={summaryId} className="visually-hidden">
          Summary
        </h2>
        <dl className="report__figures">
          <Figure label="Lines" value={report.totals.lines} />
          <Figure label="Qty ordered" value={report.totals.quantityOrdered} />
          <Figure label="Qty verified" value={report.totals.quantityVerified} />
          <Figure label="Qty cancelled" value={report.totals.quantityCancelled} />
          <Figure label="Distinct parts" value={report.totals.distinctParts} />
          <Figure label="Orders" value={report.totals.orders} />
        </dl>
      </section>

      <table className="report__table report__table--parts">
        <caption className="report__caption">By part</caption>
        <thead>
          <tr>
            <th scope="col">Part number</th>
            <th scope="col">Description</th>
            <th scope="col" className="report__numeric">
              Qty ordered
            </th>
            <th scope="col" className="report__numeric">
              Qty verified
            </th>
            <th scope="col" className="report__numeric">
              Qty cancelled
            </th>
            <th scope="col" className="report__numeric">
              Orders
            </th>
          </tr>
        </thead>
        <tbody>
          {report.byPart.map((part) => (
            <PartRow key={part.partNumber} part={part} />
          ))}
        </tbody>
      </table>

      <table className="report__table report__table--lines">
        <caption className="report__caption">Lines</caption>
        <thead>
          <tr>
            <th scope="col">Ordered at</th>
            <th scope="col">Part</th>
            <th scope="col" className="report__numeric">
              Qty ordered
            </th>
            <th scope="col" className="report__numeric">
              Qty verified
            </th>
            <th scope="col">Status</th>
            <th scope="col">Side</th>
            <th scope="col">Order</th>
            <th scope="col">Customer and vehicle</th>
            <th scope="col">People</th>
            <th scope="col">ETA</th>
          </tr>
        </thead>
        <tbody>
          {report.lines.map((line) => (
            <LineRow key={line.lineId} line={line} />
          ))}
        </tbody>
      </table>
    </>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="report__figure">
      <dt>{label}</dt>
      <dd>{formatCount(value)}</dd>
    </div>
  );
}

function PartRow({ part }: { part: StockOrderReportPart }) {
  return (
    <tr>
      <td className="report__mono">{part.partNumber}</td>
      <td>{part.description}</td>
      <td className="report__numeric">{formatCount(part.quantityOrdered)}</td>
      <td className="report__numeric">{formatCount(part.quantityVerified)}</td>
      <td className="report__numeric report__numeric--cancelled" data-zero={part.quantityCancelled === 0 ? "true" : undefined}>
        {formatCount(part.quantityCancelled)}
      </td>
      <td className="report__numeric">{formatCount(part.orders)}</td>
    </tr>
  );
}

function LineRow({ line }: { line: StockOrderReportLine }) {
  if (line.side === "wholesale") return <WholesaleLineRow line={line} />;
  return (
    <tr className="report__line" data-cancelled={line.cancelled ? "true" : undefined}>
      <td className="report__when">
        <time dateTime={line.orderedAt}>{formatReceiptInstant(line.orderedAt)}</time>
      </td>
      <td>
        <span className="report__mono report__part-number">{line.partNumber}</span>
        <span className="report__part-description">{line.description}</span>
      </td>
      <td className="report__numeric">{formatCount(line.quantityOrdered)}</td>
      <td className="report__numeric">{formatCount(line.quantityVerified)}</td>
      <td>
        <span className="report__status" data-status={line.status}>
          {lineStatusLabel(line.status)}
        </span>
        {line.cancelled ? <span className="report__flag">Cancelled</span> : null}
      </td>
      <td>
        <span className="report__side-label" data-side={line.side}>
          {sideLabel(line.side)}
        </span>
      </td>
      <td>
        <Link to={caseWorkspacePath(line.caseId)} className="report__order-link report__mono">
          {line.caseNumber}
        </Link>
        <span className="report__ro">{roLabel(line.roNumber)}</span>
        {line.vendorOrderReference ? (
          <span className="report__vendor-ref">Ref {line.vendorOrderReference}</span>
        ) : null}
      </td>
      <td>
        <span className="report__customer">{line.customerName}</span>
        <span className="report__vehicle">
          {vehicleLabel(line)} · <span className="report__mono">{vinEndingLabel(line.vinEnding)}</span>
        </span>
      </td>
      <td>
        <span className="report__person">{line.advisorName}</span>
        <span className="report__person report__person--technician">{technicianLabel(line.technicianName)}</span>
      </td>
      <td className="report__eta">{etaLabel(line.eta)}</td>
    </tr>
  );
}

/**
 * A wholesale line (wholesale spec §8): the WS order linked into the
 * wholesale workspace with the customer's reference and R&R number, the
 * customer as the "Customer and vehicle" cell — there is no vehicle — and
 * no people. The service words "Pre-RO" and "Unassigned" never appear.
 */
function WholesaleLineRow({ line }: { line: StockOrderReportLine }) {
  return (
    <tr className="report__line" data-cancelled={line.cancelled ? "true" : undefined} data-side="wholesale">
      <td className="report__when">
        <time dateTime={line.orderedAt}>{formatReceiptInstant(line.orderedAt)}</time>
      </td>
      <td>
        <span className="report__mono report__part-number">{line.partNumber}</span>
        <span className="report__part-description">{line.description}</span>
      </td>
      <td className="report__numeric">{formatCount(line.quantityOrdered)}</td>
      <td className="report__numeric">{formatCount(line.quantityVerified)}</td>
      <td>
        <span className="report__status" data-status={line.status}>
          {lineStatusLabel(line.status)}
        </span>
        {line.cancelled ? <span className="report__flag">Cancelled</span> : null}
      </td>
      <td>
        <span className="report__side-label" data-side="wholesale">
          {sideLabel("wholesale")}
        </span>
      </td>
      <td>
        <Link to={wholesaleOrderPath(line.caseId)} className="report__order-link report__mono">
          {line.caseNumber}
        </Link>
        {line.customerReference ? <span className="report__ro">Ref {line.customerReference}</span> : null}
        {line.rrNumber ? <span className="report__vendor-ref">R&amp;R {line.rrNumber}</span> : null}
      </td>
      <td>
        <span className="report__customer">{line.customerName}</span>
        <span className="report__vehicle">Wholesale customer</span>
      </td>
      <td>
        <span className="report__person report__person--none">—</span>
      </td>
      <td className="report__eta">{etaLabel(line.eta)}</td>
    </tr>
  );
}
